import { Icon } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useField, useForm, useFormState } from 'react-final-form';
import { Level3Section } from '../Level3Section';
import {
  Wrapper,
  HeaderRow,
  Title,
  StyledDialog,
  QuestionsWrapper,
  ContentWrapper,
  SectionGroupLabel,
  EmptyText
} from './components/ui';
import { Section, SectionNotes } from 'services/api/vehicle-sections/types';
import { ActivateButtonUI, useActivate } from '../components/activateButton';
import { useIsOnline } from 'hooks';
import { closeFinalReviewDialog, useFinalReviewDialogOpened } from 'services/listerUploadVehicle/v2';
import { useDispatch } from 'react-redux';
import toast, { ToastBar, Toaster } from 'react-hot-toast';
import React, { useEffect, useMemo, useState } from 'react';
import { FinalReviewNotesField } from './FinalReviewNotesField';
import { typeToLabel } from '../components/photos/constants';
import { FinalReviewObdImages } from './FinalReviewObdImages';
import { FinalReviewTabs } from './FinalReviewTabs';
import { getIn } from 'final-form';
import { useFieldArray } from 'react-final-form-arrays';

const obdSectionPath = 'interior_questions.subsections.odb_check_smog_readiness';

export const FinalReviewDialog = () => {
  const dispatch = useDispatch();

  const finalReviewOpen = useFinalReviewDialogOpened();
  const close = () => dispatch(closeFinalReviewDialog());

  return (
    <StyledDialog
      onBackdropClick={close}
      open={!!finalReviewOpen}
      isFullScreenMode={false}
      classes={{ paperScrollPaper: 'vehicle-questions' }}
    >
      {!!finalReviewOpen && <VehicleQuestionsDialogContent />}
    </StyledDialog>
  );
};

type NotesPathMetadata = {
  path: string;
  title: string;
  type?: string;
};

function getSectionsWithNotes(
  values: Record<string, Section> & { inspector_notes?: SectionNotes; is_guaranteed?: boolean },
  parentKey = '',
  parentLabel = ''
): NotesPathMetadata[] {
  const result: NotesPathMetadata[] = [];

  const { inspector_notes, vehicle, is_guaranteed, ...sections } = values;

  for (const [key, section] of Object.entries(sections)) {
    const base = parentKey ? `${parentKey}.${key}` : key;

    if (base.endsWith('_custom')) continue;

    const sectionLabel = section.label || parentLabel;

    const isDone = (() => {
      const hasNotes = !!section.notes?.message?.trim();
      if (section.checkboxes && section.checkboxes.length > 0) {
        return section.checkboxes.some(c => c.selected) && (section.grade || hasNotes);
      }
      return hasNotes && section.grade;
    })();

    if (isDone) {
      result.push({
        path: base,
        title: sectionLabel
      });
    }

    // Image notes
    section.images?.forEach((img, i) => {
      if (img.notes?.message?.trim()) {
        result.push({
          path: `${base}.images[${i}]`,
          title: img.type ? typeToLabel[img.type] : sectionLabel
        });
      }
    });

    // Checkbox notes
    section.checkboxes?.forEach((cb, i) => {
      if (cb.notes?.message?.trim()) {
        result.push({
          path: `${base}.checkboxes[${i}]`,
          title: section.label + '/' + cb.label
        });
      }
    });

    // Recursive subsections
    if (section.subsections && Object.keys(section.subsections).length > 0) {
      const subsectionResults = getSectionsWithNotes(section.subsections, `${base}.subsections`, sectionLabel);
      result.push(...subsectionResults);
    }
  }

  if (inspector_notes && inspector_notes?.message) {
    result.push({
      path: 'inspector_notes',
      title: 'Inspector Notes'
    });
  }

  return result;
}

function getSkippedSections(
  values: Record<string, Section> & { inspector_notes?: SectionNotes; is_guaranteed?: boolean },
  parentKey = '',
  parentLabel = ''
): NotesPathMetadata[] {
  const result: NotesPathMetadata[] = [];

  const { inspector_notes, vehicle, is_guaranteed, ...sections } = values;

  for (const [key, section] of Object.entries(sections)) {
    const base = parentKey ? `${parentKey}.${key}` : key;
    if (base.endsWith('_custom')) continue;

    const sectionLabel = section.label || parentLabel;

    const hasSubSections = section.subsections && Object.keys(section.subsections).length > 0;

    const hasMessage = section.notes?.message?.trim();

    if (hasSubSections) {
      const subsectionResults = getSkippedSections(section.subsections, `${base}.subsections`, sectionLabel);
      result.push(...subsectionResults);
      continue;
    }

    const isSkippedSection = (() => {
      if (key === 'odometer') return !hasMessage;
      if (section.checkboxes && section.checkboxes.length > 0) {
        return (!hasMessage && !section.grade) || !section.checkboxes.some(c => c.selected);
      }
      return !hasMessage || !section.grade;
    })();

    if (isSkippedSection) {
      result.push({
        path: base,
        title: sectionLabel
      });
    }
  }

  if (inspector_notes && !inspector_notes?.message) {
    result.push({
      path: 'inspector_notes',
      title: 'Inspector Notes'
    });
  }

  return result;
}

const VehicleQuestionsDialogContent = () => {
  const { handleList, shouldRender, listedAuctionActive } = useActivate();

  const [selectedTab, setSelectedTab] = useState('skipped');

  const dispatch = useDispatch();
  const close = () => dispatch(closeFinalReviewDialog());

  const isOnline = useIsOnline();

  const form = useFormState();
  const formApi = useForm();

  const {
    input: { value: inspectorNotes }
  } = useField(`inspector_notes.message`, { subscription: { value: true } });

  const sectionsToCheck = useMemo(() => {
    const values = formApi.getState().values || {};
    const fields = ['under_hood', 'interior_questions', 'exterior', 'drive_cycles', 'inspector_notes'];

    return fields.reduce((acc, curr) => {
      if (!values[curr]) return acc;
      return { ...acc, [curr]: values[curr] };
    }, {});
  }, [selectedTab]);

  const fieldsWithNotes = useMemo(() => {
    return getSectionsWithNotes(sectionsToCheck);
  }, [sectionsToCheck]);

  const skippedFields = useMemo(() => {
    const skippedSections = getSkippedSections(sectionsToCheck);
    const grouped = skippedSections.reduce((acc, curr) => {
      const parts = curr.path.split('.');
      const lastSubsectionI = findLastIndex(parts, 'subsections');
      const groupPart = curr.path === 'inspector_notes' ? 'drive_cycles' : parts.slice(0, lastSubsectionI).join('.');
      if (!acc[groupPart]) {
        acc[groupPart] = [curr];
      } else {
        acc[groupPart].push(curr);
      }

      return acc;
    }, {} as Record<string, NotesPathMetadata[]>);

    const formValues = formApi.getState().values;
    return Object.entries(grouped).flatMap(([key, values]) => {
      const label = getIn(formValues, `${key}.label`);
      return [
        {
          path: key,
          title: label,
          type: 'group_label'
        },
        ...values
      ];
    });
  }, [sectionsToCheck]);

  const handleSubmit = async () => {
    const values = formApi.getState().values;
    const obdImages = values?.interior_questions.subsections?.odb_check_smog_readiness?.images || [];

    if (obdImages.some(img => !img.url)) {
      toast.error('Please fill codes & smog readiness info to continue');
      return;
    }

    await handleList();
    close();
  };

  const fieldsToRender = selectedTab === 'skipped' ? skippedFields : fieldsWithNotes;
  const batchedFields = useBatchedRender(fieldsToRender, 10);

  const emptyText = selectedTab === 'skipped' ? 'No open questions remaining' : 'No completed questions';

  const {
    fields: { value }
  } = useFieldArray(`${obdSectionPath}.images`, { subscription: { value: true } });
  const hasBothImages = value?.every(img => !!img.url);

  const renderEmptyText = (() => {
    if (batchedFields.length !== 0) return false;
    return selectedTab === 'skipped' ? hasBothImages : !hasBothImages;
  })();

  return (
    <Wrapper>
      <HeaderRow>
        <Title>Final Review</Title>
        <Icon onClick={close}>
          <Close />
        </Icon>
      </HeaderRow>
      <FinalReviewTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />

      <ContentWrapper>
        <QuestionsWrapper withPadding>
          {batchedFields.map(field => {
            return <RenderField key={field.path} field={field} />;
          })}
          {renderEmptyText && <EmptyText>{emptyText}</EmptyText>}
          <FinalReviewObdImages selectedTab={selectedTab} />
        </QuestionsWrapper>

        {shouldRender ? (
          <ActivateButtonUI
            withMargin
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            type="button"
            disabled={!listedAuctionActive || !isOnline || !inspectorNotes}
          >
            Submit Vehicle
          </ActivateButtonUI>
        ) : (
          <div style={{ height: '44px' }} />
        )}
      </ContentWrapper>

      <Toaster toasterId="final-review-toaster">
        {t => (
          <ToastBar
            toast={t}
            style={{
              ...t.style
            }}
          />
        )}
      </Toaster>
    </Wrapper>
  );
};

const RenderField = React.memo(function RenderField({ field }: { field: any }) {
  if (field.type === 'group_label') return <SectionGroupLabel>{field.title}</SectionGroupLabel>;

  if (field.path === 'inspector_notes')
    return <FinalReviewNotesField field={field.path} title={field.title} type="inspector_notes" />;

  if (field.path.includes('.checkboxes'))
    return <FinalReviewNotesField field={field.path} title={field.title} type="checkbox" />;

  if (field.path.includes('.images'))
    return <FinalReviewNotesField field={field.path} title={field.title} type="image" />;

  return <Level3Section isFinalReview disableAutoFocus notesOpened theme="white" sectionId={field.path} />;
});

function useBatchedRender<T>(items: T[], batchSize = 10) {
  const [visible, setVisible] = useState<T[]>([]);

  useEffect(() => {
    setVisible([]); // reset when items change
    let index = 0;
    let cancelled = false;

    function step() {
      if (cancelled) return;
      const nextIndex = Math.min(index + batchSize, items.length);
      setVisible(items.slice(0, nextIndex));
      index = nextIndex;
      if (index < items.length) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
    return () => {
      cancelled = true;
    };
  }, [items, batchSize]);

  return visible;
}

const findLastIndex = (array: string[], val: string) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i] === val) return i;
  }
  return -1;
};
